'use client';

import { Portal } from '@common/components';
import { SvgChevrondown } from '@common/icon';
import { throttle } from 'es-toolkit';
import { motion, useAnimation } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

import { SELECTOR } from '@/constants';

interface MoreFloatingButtonProps {
  contentAreaSelector: string;
  recommendHometownNewsSelector: string;
  keyword: string;
}

export const MoreFloatingButton = ({
  contentAreaSelector,
  recommendHometownNewsSelector,
  keyword,
}: MoreFloatingButtonProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [mainElement, setMainElement] = useState<HTMLElement>();
  const [contentsElement, setContentsElement] = useState<HTMLElement>();
  const [recommendHometownNewsElement, setRecommendHometownNewsElement] =
    useState<HTMLElement>();
  const [isShow, setIsShow] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const updateIsShow = useCallback(() => {
    if (
      mainElement === undefined ||
      contentsElement === undefined ||
      recommendHometownNewsElement === undefined
    )
      return;

    const isContentsScrollOveredHalf =
      contentsElement.offsetTop + contentsElement.offsetHeight / 2 <
      mainElement.scrollTop + mainElement.offsetHeight / 2;

    const isRecommendHometownNewsListScrollOvered =
      recommendHometownNewsElement.offsetTop <
      mainElement.scrollTop + mainElement.offsetHeight;

    setIsShow(
      isContentsScrollOveredHalf && !isRecommendHometownNewsListScrollOvered,
    );
  }, [contentsElement, mainElement, recommendHometownNewsElement]);

  useEffect(() => {
    if (!isMounted) return;

    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (mainElement) {
      setMainElement(mainElement);
    }

    const contentsElement = document.getElementById(contentAreaSelector);
    if (contentsElement) {
      setContentsElement(contentsElement);
    }

    const recommendHometownNewsElement = document.getElementById(
      recommendHometownNewsSelector,
    );
    if (recommendHometownNewsElement) {
      setRecommendHometownNewsElement(recommendHometownNewsElement);
    }
  }, [isMounted, contentAreaSelector, recommendHometownNewsSelector]);

  useEffect(() => {
    if (mainElement && contentsElement && recommendHometownNewsElement) {
      updateIsShow();
    }
  }, [
    mainElement,
    contentsElement,
    recommendHometownNewsElement,
    updateIsShow,
  ]);

  useEffect(() => {
    if (mainElement === undefined) return;

    const handleScroll = throttle(() => updateIsShow(), 200);

    mainElement.addEventListener('scroll', handleScroll);

    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [mainElement, updateIsShow]);

  useEffect(() => {
    controls.start({
      opacity: isShow ? SHOW_BUTTON_OPACITY : INITIALIZE_BUTTON_OPACITY,
      bottom: isShow ? SHOW_BUTTON_BOTTOM : INITIALIZE_BUTTON_BOTTOM,
      transition: { duration: 0.2 },
    });
  }, [isShow, controls]);

  const handleClick = () => {
    if (recommendHometownNewsElement === undefined) return;

    recommendHometownNewsElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Portal renderTo={() => document.getElementById(SELECTOR.MAIN)}>
      <motion.button
        animate={controls}
        className="sticky bottom-10 mx-auto size-fit p-0"
        disabled={!isShow}
        initial={{
          opacity: INITIALIZE_BUTTON_OPACITY,
          bottom: INITIALIZE_BUTTON_BOTTOM,
        }}
        type="button"
        onClick={handleClick}
      >
        <section className="flex h-40 w-full items-center justify-center gap-4 rounded-pill bg-Gray-black px-20">
          <p className="font-700 text-Gray-white typo-body4">
            {keyword} 소식 더보기
          </p>
          <SvgChevrondown className="size-14 text-Gray-white" />
        </section>
      </motion.button>
    </Portal>
  );
};

const INITIALIZE_BUTTON_OPACITY = 0;
const INITIALIZE_BUTTON_BOTTOM = -40;
const SHOW_BUTTON_OPACITY = 1;
const SHOW_BUTTON_BOTTOM = 10;
